/**
 * Admin Layout Component
 * Main layout wrapper with sidebar navigation
 * All admin routes live under /admin/* prefix.
 */

import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { getInitials } from '../../utils/formatters';
import ChangePasswordModal from '../common/ChangePasswordModal';

const AdminLayout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') return 'Dashboard';
    if (path === '/admin/tenants') return 'Tenants';
    if (path === '/admin/signup-requests') return 'Signup Requests';
    if (path === '/admin/email-templates') return 'Email Templates';
    if (path === '/admin/compliance-documents') return 'Compliance';
    if (path.startsWith('/admin/tenants/')) return 'Tenant Details';
    if (path === '/admin/onboarding') return 'New Tenant Onboarding';
    if (path === '/admin/all-reports') return 'All Reports (troubleshooting)';
    if (path === '/admin/reports' || path.startsWith('/admin/reports')) return 'Custom Reports';
    if (path === '/admin/roadmap') return 'Platform Roadmap';
    if (path === '/admin/github-scorecard') return 'Change scorecard';
    if (path === '/admin/compliance-reports') return 'Compliance reports';
    if (path === '/admin/settings') return 'System Settings';
    if (path === '/admin/subscription-plans') return 'Subscription Plans';
    if (path === '/admin/help') return 'Help';
    return 'System Admin';
  };

  const navItems = [
    { path: '/admin', icon: Icons.Home, label: 'Dashboard' },
    { path: '/admin/signup-requests', icon: Icons.Mail, label: 'Signup Requests' },
    { path: '/admin/email-templates', icon: Icons.FileText, label: 'Email Templates' },
    { path: '/admin/compliance-documents', icon: Icons.Shield, label: 'Compliance' },
    { path: '/admin/tenants', icon: Icons.Building, label: 'Tenants' },
    { path: '/admin/onboarding', icon: Icons.Rocket, label: 'New Tenant' },
    { path: '/admin/reports', icon: Icons.BarChart, label: 'Custom Reports' },
    { path: '/admin/all-reports', icon: Icons.FileText, label: 'All Reports' },
  ];

  const systemItems = [
    { path: '/admin/roadmap', icon: Icons.LayoutGrid, label: 'Roadmap' },
    { path: '/admin/compliance-reports', icon: Icons.Shield, label: 'Compliance reports' },
    { path: '/admin/github-scorecard', icon: Icons.BarChart, label: 'Change scorecard' },
    { path: '/admin/subscription-plans', icon: Icons.CreditCard, label: 'Subscription Plans' },
    { path: '/admin/settings', icon: Icons.Settings, label: 'Settings' },
    { path: '/admin/help', icon: Icons.HelpCircle, label: 'Help' },
  ];

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <Icons.Shield size={20} />
            </div>
            <span>System Admin</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Main</div>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `nav-item ${isActive && location.pathname === item.path ? 'active' : ''}`
                }
                end={item.path === '/admin'}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">System</div>
            {systemItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {getInitials(user?.name || user?.email)}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name || user?.email}</div>
              <div className="user-role">Super Admin</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
            <button
              onClick={() => setShowChangePassword(true)}
              className="btn btn-secondary btn-sm"
              title="Change Password"
              style={{ padding: '6px 10px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '12px' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Password
            </button>
            <button
              onClick={onLogout}
              className="btn btn-secondary btn-sm"
              title="Log out"
              style={{ padding: '6px 10px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '12px' }}
            >
              <Icons.LogOut size={14} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="content-header">
          <h1 className="content-title">{getPageTitle()}</h1>
        </header>
        <div className="content-area">
          {children}
        </div>
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default AdminLayout;
